import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Lock, Ship, X } from "lucide-react";
import { formatDate, formatDateTime, formatQuantity } from "../../lib/date";
import { useToast } from "../../lib/use-toast";
import {
	useCreateStockReservation,
	useReleaseStockReservation,
	type ComponentAvailability,
	type ComponentRole,
	type LineAvailability,
} from "../../hooks/use-stock-reservations";
import styles from "./StockReservationPanel.module.css";

interface StockReservationPanelProps {
	availability: LineAvailability | undefined;
	isLoading: boolean;
	offerId: string | undefined;
	alternativeGroup: number;
	lineQuantity: number;
}

const ROLE_ORDER: ComponentRole[] = ["product", "engine", "alternator"];

function reservedForRole(
	components: ComponentAvailability[],
	role: ComponentRole,
	group: number,
): number {
	let total = 0;
	for (const c of components) {
		if (c.role !== role) continue;
		for (const r of c.ownReservations) if (r.alternativeGroup === group) total += r.quantity;
	}
	return total;
}

export function StockReservationPanel({
	availability,
	isLoading,
	offerId,
	alternativeGroup,
	lineQuantity,
}: StockReservationPanelProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	if (isLoading && !availability)
		return <div className={styles.summary}>{t("stockResLoading")}</div>;
	if (!availability || availability.components.length === 0) return null;

	const components = [...availability.components].sort(
		(a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role),
	);
	const roleFree = new Map<ComponentRole, { free: number; incoming: number }>();
	let ownTotal = 0;
	for (const c of components) {
		const cur = roleFree.get(c.role) ?? { free: 0, incoming: 0 };
		cur.free += c.free;
		cur.incoming += c.incomingFree;
		roleFree.set(c.role, cur);
		for (const r of c.ownReservations)
			if (r.alternativeGroup === alternativeGroup) ownTotal += r.quantity;
	}

	return (
		<div className={styles.panel}>
			<button type="button" className={styles.summary} onClick={() => setOpen((v) => !v)}>
				<span className={styles.summaryLabel}>{t("stockResTitle")}</span>
				{ROLE_ORDER.filter((r) => roleFree.has(r)).map((role) => {
					const v = roleFree.get(role)!;
					return (
						<span
							key={role}
							className={[styles.chip, v.free > 0 ? styles.chipOk : styles.chipEmpty].join(" ")}
						>
							{t(`stockResRole_${role}`)}: {formatQuantity(v.free)}
							{v.incoming > 0 && (
								<span className={styles.chipIncoming}>
									{" "}
									+{formatQuantity(v.incoming)} {t("stockResIncomingShort")}
								</span>
							)}
						</span>
					);
				})}
				{ownTotal > 0 && (
					<span className={[styles.chip, styles.chipOwn].join(" ")}>
						<Lock size={12} /> {t("stockResOwnShort", { qty: formatQuantity(ownTotal) })}
					</span>
				)}
				<span className={styles.toggle}>
					{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
				</span>
			</button>

			{open && (
				<div className={styles.body}>
					{!offerId && <p className={styles.hint}>{t("stockResSaveFirst")}</p>}
					{components.map((c) => (
						<ComponentRow
							key={`${c.role}-${c.stockItemId}`}
							component={c}
							offerId={offerId}
							lineStockItemId={availability.lineStockItemId}
							alternativeGroup={alternativeGroup}
							remaining={Math.max(
								0,
								lineQuantity - reservedForRole(components, c.role, alternativeGroup),
							)}
						/>
					))}
				</div>
			)}
		</div>
	);
}

const LOAD_STATUS_KEYS: Record<string, string> = {
	waiting: "stockResLoadWaiting",
	arrived: "stockResLoadArrived",
	customs_processing: "stockResLoadCustoms",
};

function ComponentRow({
	component: c,
	offerId,
	lineStockItemId,
	alternativeGroup,
	remaining,
}: {
	component: ComponentAvailability;
	offerId: string | undefined;
	lineStockItemId: string;
	alternativeGroup: number;
	remaining: number;
}) {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const createReservation = useCreateStockReservation();
	const releaseReservation = useReleaseStockReservation();
	const [qty, setQty] = useState<Record<string, string>>({});

	const maxQty = (free: number) => Math.max(0, Math.min(remaining, free));
	const qtyValue = (key: string, free: number) => qty[key] ?? String(maxQty(free));

	const changeQty = (key: string, free: number, raw: string) => {
		const n = Number(raw);
		const max = maxQty(free);
		setQty((p) => ({
			...p,
			[key]: raw !== "" && Number.isFinite(n) && n > max ? String(max) : raw,
		}));
	};

	const isValidQty = (key: string, free: number) => {
		const n = Number(qtyValue(key, free));
		return Number.isFinite(n) && n > 0 && n <= maxQty(free);
	};

	const reserve = (source: "stock" | "incoming", free: number, loadReportId?: string) => {
		if (!offerId) return;
		const key = loadReportId ?? "stock";
		const quantity = Number(qtyValue(key, free));
		if (!isValidQty(key, free)) {
			showToast(t("stockResInvalidQty", { max: formatQuantity(maxQty(free)) }), "error");
			return;
		}
		createReservation.mutate(
			{
				offerId,
				lineStockItemId,
				stockItemId: c.stockItemId,
				alternativeGroup,
				quantity,
				source,
				...(loadReportId ? { loadReportId } : {}),
			},
			{
				onSuccess: () => {
					setQty((prev) => {
						const { [key]: _removed, ...rest } = prev;
						return rest;
					});
					showToast(t("stockResReserved"), "success");
				},
				onError: (err) => showToast(err.message, "error"),
			},
		);
	};

	const release = (id: string) =>
		releaseReservation.mutate(id, {
			onSuccess: () => showToast(t("stockResReleased"), "success"),
			onError: (err) => showToast(err.message, "error"),
		});

	const canReserve = !!offerId && remaining > 0 && !createReservation.isPending;
	const ownForGroup = c.ownReservations.filter((r) => r.alternativeGroup === alternativeGroup);

	return (
		<div className={styles.component}>
			<div className={styles.componentHead}>
				<span className={styles.roleBadge}>{t(`stockResRole_${c.role}`)}</span>
				<span className={styles.itemName} title={c.name}>
					{c.code} — {c.name}
				</span>
			</div>

			<div className={styles.row}>
				<div className={styles.stats}>
					<span>
						{t("stockResOnHand")}: <b>{formatQuantity(c.onHand)}</b>
					</span>
					<span>
						{t("stockResSapCommitted")}: <b>{formatQuantity(c.sapCommitted)}</b>
					</span>
					<span>
						{t("stockResCrmReserved")}: <b>{formatQuantity(c.crmReserved)}</b>
						{c.crmReservedOffers > 0 && ` (${t("stockResOffers", { count: c.crmReservedOffers })})`}
					</span>
					<span className={c.free > 0 ? styles.freeOk : styles.freeEmpty}>
						{t("stockResFree")}: <b>{formatQuantity(c.free)}</b>
					</span>
				</div>
				{offerId && c.free > 0 && (
					<div className={styles.action}>
						<input
							type="number"
							min="0"
							step="any"
							max={maxQty(c.free)}
							className={styles.qtyInput}
							title={t("stockResMaxQty", { max: formatQuantity(maxQty(c.free)) })}
							value={qtyValue("stock", c.free)}
							onChange={(e) => changeQty("stock", c.free, e.target.value)}
						/>
						<button
							type="button"
							className={styles.reserveBtn}
							disabled={!canReserve || !isValidQty("stock", c.free)}
							onClick={() => reserve("stock", c.free)}
						>
							{t("stockResReserve")}
						</button>
					</div>
				)}
			</div>

			{c.incoming.map((s) => (
				<div key={s.loadReportId} className={styles.row}>
					<div className={styles.stats}>
						<span className={styles.incomingLabel}>
							<Ship size={13} /> {t(LOAD_STATUS_KEYS[s.status] ?? "stockResLoadWaiting")}:{" "}
							<b>{formatQuantity(s.quantity)}</b>
						</span>
						<span>
							{t("stockResEta")}: {formatDate(s.estimatedArrivalDate)}
							{s.arrivalPort ? ` · ${s.arrivalPort}` : ""}
						</span>
						{s.reserved > 0 && (
							<span>
								{t("stockResCrmReserved")}: {formatQuantity(s.reserved)}
							</span>
						)}
						<span className={s.free > 0 ? styles.freeOk : styles.freeEmpty}>
							{t("stockResFree")}: <b>{formatQuantity(s.free)}</b>
						</span>
					</div>
					{offerId && s.free > 0 && (
						<div className={styles.action}>
							<input
								type="number"
								min="0"
								step="any"
								max={maxQty(s.free)}
								className={styles.qtyInput}
								title={t("stockResMaxQty", { max: formatQuantity(maxQty(s.free)) })}
								value={qtyValue(s.loadReportId, s.free)}
								onChange={(e) => changeQty(s.loadReportId, s.free, e.target.value)}
							/>
							<button
								type="button"
								className={styles.reserveBtn}
								disabled={!canReserve || !isValidQty(s.loadReportId, s.free)}
								onClick={() => reserve("incoming", s.free, s.loadReportId)}
							>
								{t("stockResReserve")}
							</button>
						</div>
					)}
				</div>
			))}

			{ownForGroup.map((r) => (
				<div key={r.id} className={styles.own}>
					<Lock size={12} />
					<span>
						{t("stockResOwnLine", {
							qty: formatQuantity(r.quantity),
							source: t(r.source === "stock" ? "stockResSourceStock" : "stockResSourceIncoming"),
							until: formatDateTime(r.expiresAt),
						})}
					</span>
					<button
						type="button"
						className={styles.releaseBtn}
						disabled={releaseReservation.isPending}
						onClick={() => release(r.id)}
						title={t("stockResRelease")}
					>
						<X size={12} /> {t("stockResRelease")}
					</button>
				</div>
			))}
		</div>
	);
}
